import { spawn } from 'node:child_process';
import { createLogger } from '../logging/index.js';
import { ok, err } from '../types/result.js';
import type { Result } from '../types/result.js';

const logger = createLogger('git-pull');

export interface PullOptions {
  repoPath: string;
  branch?: string | undefined;
  /** Pull timeout in milliseconds (default: 60000) */
  timeoutMs?: number | undefined;
}

const DEFAULT_PULL_TIMEOUT_MS = 60000; // 1 minute
const FORCE_KILL_DELAY_MS = 5000; // 5 seconds after SIGTERM

export async function pullRepository(options: PullOptions): Promise<Result<string>> {
  const { repoPath, branch, timeoutMs = DEFAULT_PULL_TIMEOUT_MS } = options;

  const args = ['pull', 'origin'];
  if (branch !== undefined) {
    args.push(branch);
  }

  logger.info({ repoPath, branch }, 'Starting git pull');

  return new Promise((resolve) => {
    const git = spawn('git', args, { cwd: repoPath, stdio: ['ignore', 'pipe', 'pipe'] });
    let timedOut = false;
    let forceKillTimeout: NodeJS.Timeout | null = null;

    const timeout = setTimeout(() => {
      timedOut = true;
      git.kill('SIGTERM');
      forceKillTimeout = setTimeout(() => {
        if (!git.killed) {
          git.kill('SIGKILL');
        }
      }, FORCE_KILL_DELAY_MS);
    }, timeoutMs);

    let stderr = '';
    git.stderr.on('data', (data: Buffer) => {
      stderr += data.toString();
    });

    let stdout = '';
    git.stdout.on('data', (data: Buffer) => {
      stdout += data.toString();
    });

    git.on('error', (error: Error) => {
      clearTimeout(timeout);
      if (forceKillTimeout) clearTimeout(forceKillTimeout);
      resolve(err(error));
    });

    git.on('close', (code: number | null) => {
      clearTimeout(timeout);
      if (forceKillTimeout) clearTimeout(forceKillTimeout);
      if (timedOut) {
        resolve(err(new Error(`Git pull timed out after ${String(timeoutMs)}ms in: ${repoPath}`)));
      } else if (code === 0) {
        logger.info({ repoPath, output: stdout.trim() }, 'Git pull completed');
        resolve(ok(stdout.trim()));
      } else {
        resolve(err(new Error(`Git pull failed: ${stderr}`)));
      }
    });
  });
}
